import {
    RequestService,
    RequestServiceResponse,
} from '@tcc-online/request-service';
import ProjectInvitation from '../types/invitation/ProjectInvitation';
import ProjectInvitationParticipant from '../types/invitation/ProjectInvitationParticipant';
import ProjectInvitationRecap from '../types/invitation/ProjectInvitationRecap';
import SortParams from '../types/SortParams';

const createBaseUrl = (
    productId: number,
    projectId: number,
    append: string = '',
) => {
    return `/admin/products/${productId}/projects/${projectId}/invitations${
        append ? `/${append}` : ''
    }`;
};

async function createInvite(payload: {
    productId: number;
    projectId: number;
    name?: string;
}): Promise<RequestServiceResponse<{ id: number }>> {
    const { productId, projectId, name } = payload;
    return await RequestService.post<
        { name?: string },
        { name?: string; id: number }
    >(createBaseUrl(productId, projectId), {
        name,
    });
}

async function getInvite(payload: {
    productId: number;
    projectId: number;
    inviteId: number;
    locale?: string;
}): Promise<RequestServiceResponse<ProjectInvitation>> {
    const { productId, projectId, inviteId, locale } = payload;

    return await RequestService.get<ProjectInvitation>(
        createBaseUrl(
            productId,
            projectId,
            `${inviteId.toString()}${locale ? `?locale=${locale}` : ''}`,
        ),
    );
}

async function getInvites(
    payload: {
        productId: number;
        projectId: number;
        limit?: number;
    } & SortParams,
): Promise<RequestServiceResponse<ProjectInvitation[]>> {
    const { productId, projectId, limit = 100, sortBy, sortOrder } = payload;

    return await RequestService.get<ProjectInvitation[]>(
        createBaseUrl(productId, projectId),
        { limit, sortOrder, sortBy },
    );
}

async function saveInvite(payload: {
    productId: number;
    projectId: number;
    inviteId: number;
    multilingual?: boolean;
    subject: string;
    content: string;
    locale?: string;
}): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId, inviteId, locale, ...body } = payload;

    const baseUrl = createBaseUrl(productId, projectId, inviteId.toString());
    const url = locale ? `${baseUrl}?locale=${locale}` : baseUrl;

    return await RequestService.patch<unknown>(url, body);
}

async function getParticipants(payload: {
    productId: number;
    projectId: number;
    inviteId: number;
}): Promise<RequestServiceResponse<ProjectInvitationParticipant[]>> {
    const { productId, projectId, inviteId } = payload;
    return await RequestService.get<ProjectInvitationParticipant[]>(
        createBaseUrl(
            productId,
            projectId,
            `${inviteId.toString()}/participants`,
        ),
    );
}

async function changeParticipantLanguage(payload: {
    productId: number;
    projectId: number;
    inviteId: number;
    participantId: number;
    locale: string;
}): Promise<RequestServiceResponse<ProjectInvitationParticipant>> {
    const { productId, projectId, inviteId, participantId, locale } = payload;
    return await RequestService.patch<unknown, ProjectInvitationParticipant>(
        createBaseUrl(
            productId,
            projectId,
            `${inviteId.toString()}/participants/${participantId}`,
        ),
        { locale },
    );
}

async function selectParticipant(payload: {
    productId: number;
    projectId: number;
    inviteId: number;
    participantId: number;
}): Promise<RequestServiceResponse<ProjectInvitationParticipant[]>> {
    const { productId, projectId, inviteId, participantId } = payload;
    return await RequestService.post<unknown, ProjectInvitationParticipant[]>(
        createBaseUrl(
            productId,
            projectId,
            `${inviteId.toString()}/participants`,
        ),
        [{ id: participantId }],
    );
}

async function selectParticipants(payload: {
    productId: number;
    projectId: number;
    inviteId: number;
    participantIds: { id: number }[];
}): Promise<RequestServiceResponse<ProjectInvitationParticipant[]>> {
    const { productId, projectId, inviteId, participantIds } = payload;
    return await RequestService.post<unknown, ProjectInvitationParticipant[]>(
        createBaseUrl(
            productId,
            projectId,
            `${inviteId.toString()}/participants`,
        ),
        participantIds,
    );
}

async function deselectParticipant(payload: {
    productId: number;
    projectId: number;
    inviteId: number;
    participantId: number;
}): Promise<RequestServiceResponse<ProjectInvitationParticipant>> {
    const { productId, projectId, inviteId, participantId } = payload;
    return await RequestService.delete<ProjectInvitationParticipant>(
        createBaseUrl(
            productId,
            projectId,
            `${inviteId.toString()}/participants/${participantId}`,
        ),
    );
}

async function deselectParticipants(payload: {
    productId: number;
    projectId: number;
    inviteId: number;
}): Promise<RequestServiceResponse<ProjectInvitationParticipant>> {
    const { productId, projectId, inviteId } = payload;
    return await RequestService.put<unknown, ProjectInvitationParticipant>(
        createBaseUrl(
            productId,
            projectId,
            `${inviteId.toString()}/participants`,
        ),
        [],
    );
}

async function changeName(payload: {
    productId: number;
    projectId: number;
    inviteId: number;
    name: string;
}): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId, inviteId, name } = payload;

    return await RequestService.patch<unknown>(
        createBaseUrl(productId, projectId, inviteId.toString()),
        { name },
        { allowMultiRequest: true },
    );
}

async function getRecaps(payload: {
    productId: number;
    projectId: number;
    inviteId: number;
}): Promise<RequestServiceResponse<ProjectInvitationRecap[]>> {
    const { productId, projectId, inviteId } = payload;
    return await RequestService.get<ProjectInvitationRecap[]>(
        createBaseUrl(productId, projectId, `${inviteId.toString()}/recaps`),
    );
}

async function setMultilingual(payload: {
    productId: number;
    projectId: number;
    inviteId: number;
    multilingual: boolean;
}): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId, inviteId, multilingual } = payload;

    return await RequestService.patch<unknown>(
        createBaseUrl(productId, projectId, inviteId.toString()),
        { multilingual },
    );
}

async function sendInvite(payload: {
    productId: number;
    projectId: number;
    inviteId: number;
}): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId, inviteId } = payload;

    return await RequestService.patch<unknown>(
        createBaseUrl(productId, projectId, inviteId.toString()),
        { state: 1 },
    );
}

const AdminInviteService = {
    createInvite,
    getInvite,
    getInvites,
    saveInvite,
    getParticipants,
    changeParticipantLanguage,
    selectParticipant,
    selectParticipants,
    deselectParticipant,
    deselectParticipants,
    changeName,
    getRecaps,
    setMultilingual,
    sendInvite,
};

export default AdminInviteService;
